import { Component, EventEmitter, Output, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface DistributorItem {
  id: string;
  distributor_name: string;
  email: string;
  isSelected: boolean;
  retailerCode: string;
}

@Component({
  selector: 'app-assign-distributor-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './assign-distributor-modal.component.html',
  styleUrls: ['./assign-distributor-modal.component.scss']
})
export class AssignDistributorModalComponent implements OnInit, OnDestroy {
  @Input() retailerId: string = '';
  @Input() retailerName: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() assignDistributors = new EventEmitter<{ retailerId: string; assignments: { distributorId: string; retailerCode: string }[] }>();

  searchTerm = '';
  submitting = false;
  loading = false;
  loadingExisting = true;

  // Separate map to persist selections across searches
  selectedMap = new Map<string, { distributor: DistributorItem; retailerCode: string }>();

  // Distributors fetched from current API call (visible in list)
  displayedDistributors: DistributorItem[] = [];

  // Debounce search
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(private http: HttpClient) {}

  ngOnInit() {
    // Set up debounced search (300ms delay)
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.fetchDistributors(term);
    });

    // Fetch existing assignments first, then load initial distributors
    this.fetchExistingAssignments();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  fetchExistingAssignments() {
    this.loadingExisting = true;

    this.http.get<any>(
      `${environment.apiBaseUrl}/admin/retailer/${this.retailerId}/distributors`
    ).subscribe({
      next: (res) => {
        this.loadingExisting = false;
        if (res.statusCode === 200 && res.data) {
          // Pre-populate the selectedMap with existing assignments
          for (const assignment of res.data) {
            const dist: DistributorItem = {
              id: assignment.distributor?.id || assignment.distributor_id,
              distributor_name: assignment.distributor?.distributor_name || '',
              email: assignment.distributor?.email || '',
              isSelected: true,
              retailerCode: assignment.customer_code || '',
            };
            this.selectedMap.set(dist.id, {
              distributor: dist,
              retailerCode: dist.retailerCode,
            });
          }
        }
        // Now fetch the initial list of distributors
        this.fetchDistributors('');
      },
      error: () => {
        this.loadingExisting = false;
        // Even if fetching existing fails, still load distributors list
        this.fetchDistributors('');
      }
    });
  }

  fetchDistributors(search: string) {
    this.loading = true;
    let url = `${environment.apiBaseUrl}/admin/distributors?limit=10`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }

    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.statusCode === 200 && res.data?.distributors) {
          this.displayedDistributors = res.data.distributors.map((d: any) => {
            // Restore selection state if this distributor was previously selected
            const existing = this.selectedMap.get(d.id);
            return {
              id: d.id,
              distributor_name: d.distributor_name,
              email: d.email,
              isSelected: !!existing,
              retailerCode: existing?.retailerCode || ''
            };
          });
        } else {
          this.displayedDistributors = [];
        }
      },
      error: () => {
        this.loading = false;
        this.displayedDistributors = [];
      }
    });
  }

  onSearch() {
    this.searchSubject.next(this.searchTerm.trim());
  }

  clearSearch() {
    this.searchTerm = '';
    this.fetchDistributors('');
  }

  onSelectionChange(dist: DistributorItem) {
    if (dist.isSelected) {
      // Add to selection map
      this.selectedMap.set(dist.id, {
        distributor: { ...dist },
        retailerCode: dist.retailerCode
      });
    } else {
      // Remove from selection map and clear retailer code
      this.selectedMap.delete(dist.id);
      dist.retailerCode = '';
    }
  }

  onRetailerCodeChange(dist: DistributorItem) {
    // Keep the selection map in sync with retailer code changes
    if (this.selectedMap.has(dist.id)) {
      this.selectedMap.get(dist.id)!.retailerCode = dist.retailerCode;
    }
  }

  getSelectedCount(): number {
    return this.selectedMap.size;
  }

  getSelectedDistributors(): { id: string; distributor_name: string; retailerCode: string }[] {
    return Array.from(this.selectedMap.values()).map(entry => ({
      id: entry.distributor.id,
      distributor_name: entry.distributor.distributor_name,
      retailerCode: entry.retailerCode
    }));
  }

  trackByDistId(index: number, item: DistributorItem): string {
    return item.id;
  }

  errorMsg = '';

  onSubmit() {
    this.errorMsg = '';

    // Validate that all selected distributors have a retailer code
    if (this.selectedMap.size > 0) {
      const missing = Array.from(this.selectedMap.values()).filter(
        entry => !entry.retailerCode || !entry.retailerCode.trim()
      );
      if (missing.length > 0) {
        this.errorMsg = `Retailer Code is required for all selected distributors.`;
        return;
      }
    }

    this.submitting = true;

    const assignments = Array.from(this.selectedMap.values()).map(entry => ({
      distributorId: entry.distributor.id,
      retailerCode: entry.retailerCode.trim()
    }));

    this.assignDistributors.emit({
      retailerId: this.retailerId,
      assignments
    });
  }

  resetSubmitting() {
    this.submitting = false;
  }

  onClose() {
    this.close.emit();
  }
}
